import React, {useState, useEffect} from 'react';
import {
  Layer,
  view,
  SelectField,
  SelectFieldItem,
  TextField,
  tokens,
} from '@sail/ui';
import {useIntl} from '@sail/intl';
import {Text, BrandedButton, Banner} from 'src/v2/components/common';
import {messages} from 'src/v2/components/directDeposit/refundAddressMessages';
import {getNetworkName} from 'src/components/Wallet/constants';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum} from '@stripe-internal/data-v1/schema/types';

import type {RefundMethod} from 'src/v2/state/types';

const DISABLED_COLOR = tokens.color.neutral[400].toString();

type RefundAddressDialogProps = {
  isOpen: boolean;
  onApply: (params: {
    refundMethod: RefundMethod;
    customRefundAddress: string;
  }) => Promise<void>;
  initialCustomRefundAddress: string;
  initialRefundMethod: RefundMethod | null;
  selectedNetwork: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
  loading: boolean;
};

export const RefundAddressDialog = ({
  isOpen,
  onApply,
  initialCustomRefundAddress,
  initialRefundMethod,
  selectedNetwork,
  loading,
}: RefundAddressDialogProps) => {
  const intl = useIntl();
  const [localRefundMethod, setLocalRefundMethod] =
    useState<RefundMethod | null>(initialRefundMethod);
  const [localCustomRefundAddress, setLocalCustomRefundAddress] = useState(
    initialCustomRefundAddress,
  );
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (isOpen) {
      setLocalRefundMethod(initialRefundMethod);
      setLocalCustomRefundAddress(initialCustomRefundAddress);
      setValidationError(undefined);
    }
  }, [isOpen, initialCustomRefundAddress, initialRefundMethod]);

  if (!isOpen) {
    return null;
  }

  const handleApply = async () => {
    if (!localRefundMethod) return;
    try {
      await onApply({
        refundMethod: localRefundMethod,
        customRefundAddress: localCustomRefundAddress,
      });
    } catch {
      setValidationError(intl.formatMessage(messages.refundAddressSaveError));
    }
  };

  const isApplyDisabled =
    !localRefundMethod ||
    (localRefundMethod === 'setNewRefundAddress' &&
      !localCustomRefundAddress.trim()) ||
    !!validationError;

  return (
    <Layer name="refund-address-dialog">
      {/* Backdrop — always non-interactive; dialog must be closed via Cancel or Apply */}
      <view.div
        css={{
          position: 'fixed',
          inset: 'space.0',
          backgroundColor: 'rgba(0, 0, 0, 0.60)',
          zIndex: 1,
        }}
      />

      {/* Card */}
      <view.div
        css={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          width: '360px',
          backgroundColor: 'surface',
          borderRadius: 'medium',
          padding: 'large',
          stack: 'y',
          gap: 'medium',
          boxShadow:
            '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Text type="title">
          {intl.formatMessage(messages.preferredRefundMethod)}
        </Text>

        {/* Refund method selector */}
        <SelectField
          label={
            <Text type="input">
              {intl.formatMessage(messages.refundMethodLabel)}
            </Text>
          }
          selectedKey={localRefundMethod ?? undefined}
          placeholder={intl.formatMessage(messages.selectRefundMethod)}
          onSelectionChange={(key) => {
            setLocalRefundMethod(key as RefundMethod);
            setValidationError(undefined);
          }}
          size="small"
          subviews={{
            button: {
              css: {
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 400,
                color: localRefundMethod ? 'text' : DISABLED_COLOR,
              },
            },
          }}
        >
          <SelectFieldItem id="sendToPaymentAddress">
            <Text type="body">
              {intl.formatMessage(messages.sendToPaymentAddress)}
            </Text>
          </SelectFieldItem>
          <SelectFieldItem id="setNewRefundAddress">
            <Text type="body">
              {intl.formatMessage(messages.setNewRefundAddress)}
            </Text>
          </SelectFieldItem>
        </SelectField>

        {localRefundMethod === 'sendToPaymentAddress' ? (
          <Banner
            variant="warning"
            message={intl.formatMessage(messages.exchangeWarning)}
          />
        ) : null}

        {localRefundMethod === 'setNewRefundAddress' ? (
          <>
            {/* Address input */}
            <view.div css={{stack: 'y', gap: 'xsmall'}}>
              <TextField
                label={intl.formatMessage(messages.newRefundAddressLabel)}
                value={localCustomRefundAddress}
                onChange={(e) => {
                  setLocalCustomRefundAddress(e.target.value);
                  setValidationError(undefined);
                }}
                placeholder={intl.formatMessage(
                  messages.newRefundAddressPlaceholder,
                )}
              />

              <Text
                type="caption"
                css={{
                  color: 'subdued',
                  marginTop: 'xxsmall',
                }}
              >
                {intl.formatMessage(messages.refundNetworkLabel, {
                  networkName: getNetworkName(selectedNetwork),
                })}
              </Text>
            </view.div>

            {/* Network mismatch disclaimer */}
            <Banner
              variant="warning"
              message={intl.formatMessage(messages.networkMismatchDisclaimer)}
            />
          </>
        ) : null}

        {validationError ? (
          <Text type="caption" css={{color: 'feedback.critical'}}>
            {validationError}
          </Text>
        ) : null}

        <BrandedButton
          onClick={handleApply}
          loading={loading}
          disabled={isApplyDisabled}
        >
          {intl.formatMessage(messages.apply)}
        </BrandedButton>
      </view.div>
    </Layer>
  );
};
